import axios from 'axios';
import { resolveGeneralNotifBase } from '@/utils/general-notif-url';

// Cache giving API URL to avoid repeated string operations
const getGivingApiUrl = (() => {
  let cachedUrl = null;
  return () => {
    if (!cachedUrl) {
      const baseUrl =
        process.env.VUE_APP_GIVING_API_V2_URL ||
        process.env.VUE_APP_GIVING_API_URL ||
        'http://localhost:8080';
      cachedUrl = baseUrl.replace(/\/api\/?$/, '') + '/api/notifications/get';
    }
    return cachedUrl;
  };
})();

// Cache giving API base URL for mark-read endpoint
const getGivingApiBaseUrl = (() => {
  let cachedUrl = null;
  return () => {
    if (!cachedUrl) {
      const baseUrl =
        process.env.VUE_APP_GIVING_API_V2_URL ||
        process.env.VUE_APP_GIVING_API_URL ||
        'http://localhost:8080';
      cachedUrl = baseUrl.replace(/\/api\/?$/, '') + '/api';
    }
    return cachedUrl;
  };
})();

// General-notification service (in-app broadcasts). Mirrors the giving-api
// wiring: this service serves the SAME per-member row contract. Both getters
// return null when the service isn't configured (see resolveGeneralNotifBase),
// and callers skip the request rather than send it to localhost.
const generalNotifBase = resolveGeneralNotifBase(process.env);
let warnedGeneralNotifMissing = false;
const warnGeneralNotifMissing = () => {
  if (warnedGeneralNotifMissing) return;
  warnedGeneralNotifMissing = true;
  console.warn(
    'VUE_APP_GENERAL_NOTIF_URL is not set in this build: general notifications are disabled.',
  );
};
const getGeneralNotifUrl = () =>
  generalNotifBase ? `${generalNotifBase}/api/notifications/get` : null;
const getGeneralNotifBaseUrl = () => (generalNotifBase ? `${generalNotifBase}/api` : null);

const INITIAL_STATE = {
  unreadCount: 0,
  updates: [],
  todaysUpdates: [],
  earlierUpdates: [],
  updateTypes: [],
  activeButton: 'all',
  // Add unread counts by type for O(1) lookup
  unreadCountsByType: {},
  isLoading: false,
};

const state = () => INITIAL_STATE;

const mutations = {
  setUnreadCount(state, count) {
    state.unreadCount = count;
  },
  setUpdates(state, updates) {
    state.updates = updates;
  },
  setTodaysUpdates(state, todaysUpdates) {
    state.todaysUpdates = todaysUpdates;
  },
  setEarlierUpdates(state, earlierUpdates) {
    state.earlierUpdates = earlierUpdates;
  },
  setUpdateTypes(state, updateTypes) {
    state.updateTypes = updateTypes;
  },
  setActiveButton(state, activeButton) {
    state.activeButton = activeButton;
  },
  setUnreadCountsByType(state, counts) {
    state.unreadCountsByType = counts;
  },
  setIsLoading(state, isLoading) {
    state.isLoading = isLoading;
  },
  // Optimized mutation to update a single update in place
  updateUpdateById(state, { id, updates }) {
    const updateIndex = updates.findIndex(u => u.id === id || u.id === String(id));
    if (updateIndex !== -1) {
      updates[updateIndex] = { ...updates[updateIndex], isNew: false };
    }
  },
};

// On a cold start getUpdates runs before the profile lands, when hasNoMember still reads
// false, so the member-only feeds went out and answered 403. Wait for it (fetchProfile
// shares the request in the air); a failed load falls through to the member path, as before.
const profileKnown = profile => Boolean(profile?.id) || profile?.hasNoMember === true;
const waitForProfile = (profile, dispatch) =>
  profileKnown(profile)
    ? Promise.resolve()
    : dispatch('profile/fetchProfile', null, { root: true }).catch(() => {});

// Helper function to normalize update ID for comparison
const normalizeId = id => String(id);

// Helper function to get date timestamp (cached per call)
const getDateTimestamp = date => {
  if (!date) return 0;
  return new Date(date).getTime();
};

// Helper function to check if date is today
const isToday = date => {
  if (!date) return false;
  const currentDate = new Date().setHours(0, 0, 0, 0);
  const updateDate = new Date(date).setHours(0, 0, 0, 0);
  return updateDate === currentDate;
};

// Extracted helper function for filtering and committing updates (single pass)
function filterAndCommitUpdates(commit, allUpdates, type) {
  const typesArray = [{ name: 'All', value: 'all', priority: 0 }];
  const todaysUpdates = [];
  const earlierUpdates = [];
  const unreadCountsByType = { all: 0 };

  // Cache current date calculation
  const currentDate = new Date().setHours(0, 0, 0, 0);

  // Use Set for O(1) type checking
  const seenTypes = new Set(['all']);

  // Single pass through all updates
  for (const update of allUpdates) {
    const updateDate = new Date(update.postedTime || update.sent_at || update.created_at).setHours(
      0,
      0,
      0,
      0,
    );
    const isEarlierUpdate = updateDate < currentDate;
    const updateTypeValue = update.updateType?.updateType;
    const matchesType = type === 'all' || updateTypeValue === type;

    // Sort updates into today and earlier (only if matches filter type)
    if (matchesType) {
      if (isEarlierUpdate) {
        earlierUpdates.push(update);
      } else {
        todaysUpdates.push(update);
      }
    }

    // Add unique update types (O(1) check with Set)
    if (updateTypeValue && !seenTypes.has(updateTypeValue)) {
      seenTypes.add(updateTypeValue);
      const capitalized = updateTypeValue.charAt(0).toUpperCase() + updateTypeValue.slice(1);
      typesArray.push({
        name: capitalized,
        value: updateTypeValue,
        priority: update.updateType?.priority || 0,
      });
    }

    // Count unread updates by type
    if (update.isNew) {
      unreadCountsByType.all++;
      if (updateTypeValue) {
        unreadCountsByType[updateTypeValue] = (unreadCountsByType[updateTypeValue] || 0) + 1;
      }
    }
  }

  // Sort types by priority
  typesArray.sort((a, b) => a.priority - b.priority);

  commit('setUpdates', allUpdates);
  commit('setUpdateTypes', typesArray);
  commit('setEarlierUpdates', earlierUpdates);
  commit('setTodaysUpdates', todaysUpdates);
  commit('setUnreadCount', unreadCountsByType.all);
  commit('setUnreadCountsByType', unreadCountsByType);
  commit('setActiveButton', type);
}

const UNREAD_COUNT_CACHE_KEY = 'hogc_unreadCount';

const actions = {
  async getUpdates({ commit, dispatch, rootState }, type) {
    // Populate unread count from cache instantly for the notification badge
    try {
      const cached = localStorage.getItem(UNREAD_COUNT_CACHE_KEY);
      if (cached !== null) commit('setUnreadCount', parseInt(cached, 10));
    } catch (_) {}

    try {
      commit('setIsLoading', true);
      const accessToken = rootState.user.token;
      await waitForProfile(rootState.profile, dispatch);
      // A login still waiting for its link has no member, and both notification services
      // address members only (they answer 403), so they are skipped; CMS updates still load.
      const memberFeed = request =>
        rootState.profile?.hasNoMember === true ? Promise.resolve({ data: [] }) : request();

      // Always fetch fresh data from API
      // Call all notification sources in parallel
      const [cmsUpdatesResponse, givingNotificationsResponse, generalNotificationsResponse] =
        await Promise.allSettled([
          // CMS updates from hogc-one-api
          axios.get(process.env.VUE_APP_API_URL + '/updates/get', {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
          // Notifications from giving-api (using cached URL)
          memberFeed(() =>
            axios
              .get(getGivingApiUrl(), {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              })
              .catch(error => {
                // Log error but don't fail the entire request if giving-api is unavailable
                console.warn('Failed to fetch notifications from giving-api:', error.message);
                return { data: [] };
              }),
          ),
          // General broadcast notifications from hogc-one-general-notif.
          // Not configured in this build → no request at all (never localhost).
          memberFeed(() =>
            getGeneralNotifUrl()
              ? axios
                  .get(getGeneralNotifUrl(), {
                    headers: {
                      Authorization: `Bearer ${accessToken}`,
                    },
                  })
                  .catch(error => {
                    // Failure-tolerant: don't fail the feed if the general service is down
                    console.warn('Failed to fetch general notifications:', error.message);
                    return { data: [] };
                  })
              : (warnGeneralNotifMissing(), Promise.resolve({ data: [] })),
          ),
        ]);

      // Extract data from responses
      let cmsUpdates = [];
      if (cmsUpdatesResponse.status === 'fulfilled' && cmsUpdatesResponse.value.status === 200) {
        cmsUpdates = cmsUpdatesResponse.value.data || [];
      } else if (cmsUpdatesResponse.status === 'rejected') {
        console.error('Failed to fetch CMS updates:', cmsUpdatesResponse.reason);
      }

      let givingNotifications = [];
      if (
        givingNotificationsResponse.status === 'fulfilled' &&
        givingNotificationsResponse.value.data
      ) {
        givingNotifications = givingNotificationsResponse.value.data || [];
      } else if (givingNotificationsResponse.status === 'rejected') {
        console.warn(
          'Failed to fetch giving notifications:',
          givingNotificationsResponse.reason?.message || 'Unknown error',
        );
      }

      let generalNotifications = [];
      if (
        generalNotificationsResponse.status === 'fulfilled' &&
        generalNotificationsResponse.value.data
      ) {
        generalNotifications = generalNotificationsResponse.value.data || [];
      } else if (generalNotificationsResponse.status === 'rejected') {
        console.warn(
          'Failed to fetch general notifications:',
          generalNotificationsResponse.reason?.message || 'Unknown error',
        );
      }

      if (
        cmsUpdatesResponse.status === 'rejected' &&
        givingNotificationsResponse.status === 'rejected' &&
        generalNotificationsResponse.status === 'rejected'
      ) {
        throw new Error('Failed in getting updates from all notification sources');
      }

      // Transform giving notifications to match CMS format (single pass)
      const transformedGivingNotifications = givingNotifications.map(notification => {
        // Extract link - check multiple possible locations
        const link =
          notification.link ||
          notification.notification_recipients?.link ||
          notification.notificationRecipients?.link ||
          notification.recipient?.link ||
          null;

        return {
          ...notification,
          postedTime: notification.postedTime || notification.sent_at || notification.created_at,
          updateType: notification.updateType || null,
          descriptionShort: notification.body || notification.descriptionShort || '',
          isNew: notification.status !== 'clicked',
          source: 'giving-api',
          link: link, // Explicitly set link field
        };
      });

      // Transform general notifications to match CMS card format (single pass).
      // Same per-member row contract as giving-api; only the source tag differs.
      const transformedGeneralNotifications = generalNotifications.map(notification => {
        const link =
          notification.link ||
          notification.notification_recipients?.link ||
          notification.recipient?.link ||
          null;

        return {
          ...notification,
          postedTime: notification.postedTime || notification.sent_at || notification.created_at,
          updateType: notification.updateType || null,
          descriptionShort: notification.body || notification.descriptionShort || '',
          isNew: notification.status !== 'clicked',
          source: 'general',
          link: link,
        };
      });

      // Combine CMS updates, giving notifications, and general notifications
      const allUpdates = [
        ...cmsUpdates,
        ...transformedGivingNotifications,
        ...transformedGeneralNotifications,
      ];

      // Sort chronologically by postedTime descending (most recent first)
      // Pre-compute timestamps for better performance
      allUpdates.forEach(update => {
        update._timestamp = getDateTimestamp(
          update.postedTime || update.sent_at || update.created_at,
        );
      });
      allUpdates.sort((a, b) => (b._timestamp || 0) - (a._timestamp || 0));
      // Clean up temporary timestamp property
      allUpdates.forEach(update => delete update._timestamp);

      // Process updates in single pass
      filterAndCommitUpdates(commit, allUpdates, type);
      // Cache unread count for instant badge display on next app launch
      try {
        const unreadCount = allUpdates.filter(u => u.isNew).length;
        localStorage.setItem(UNREAD_COUNT_CACHE_KEY, String(unreadCount));
      } catch (_) {}
      commit('setIsLoading', false);
    } catch (e) {
      commit('setIsLoading', false);
      dispatch('toast/error', 'Failed to get updates.', {
        root: true,
      });
    }
  },

  // Local filtering without refetching
  filterUpdatesLocally({ commit, state }, type) {
    if (!state.updates || state.updates.length === 0) {
      return;
    }

    const todaysUpdates = [];
    const earlierUpdates = [];
    const currentDate = new Date().setHours(0, 0, 0, 0);

    for (const update of state.updates) {
      const updateTypeValue = update.updateType?.updateType;
      const matchesType = type === 'all' || updateTypeValue === type;

      if (matchesType) {
        const updateDate = new Date(
          update.postedTime || update.sent_at || update.created_at,
        ).setHours(0, 0, 0, 0);
        if (updateDate < currentDate) {
          earlierUpdates.push(update);
        } else {
          todaysUpdates.push(update);
        }
      }
    }

    commit('setEarlierUpdates', earlierUpdates);
    commit('setTodaysUpdates', todaysUpdates);
    commit('setActiveButton', type);
  },

  async markReadUpdate({ commit, dispatch, rootState, state }, updateId) {
    try {
      const normalizedId = normalizeId(updateId);

      // Use Map for O(1) lookups instead of multiple findIndex calls
      const updateMap = new Map();
      const todayMap = new Map();
      const earlierMap = new Map();

      // Build maps once
      state.updates.forEach((update, index) => {
        const id = normalizeId(update.id);
        updateMap.set(id, { update, index, array: 'updates' });
      });

      state.todaysUpdates.forEach((update, index) => {
        const id = normalizeId(update.id);
        todayMap.set(id, { update, index, array: 'todays' });
      });

      state.earlierUpdates.forEach((update, index) => {
        const id = normalizeId(update.id);
        earlierMap.set(id, { update, index, array: 'earlier' });
      });

      // Find update in O(1) time
      const updateInfo = updateMap.get(normalizedId);
      if (!updateInfo) {
        return; // Update not found
      }

      // Optimistically update all arrays
      const updatedUpdates = [...state.updates];
      const updatedTodays = [...state.todaysUpdates];
      const updatedEarlier = [...state.earlierUpdates];

      let unreadCountDecrement = 0;

      // Update main updates array
      if (updatedUpdates[updateInfo.index].isNew) {
        updatedUpdates[updateInfo.index] = { ...updatedUpdates[updateInfo.index], isNew: false };
        unreadCountDecrement = 1;
      }
      commit('setUpdates', updatedUpdates);

      // Update todaysUpdates if present
      const todayInfo = todayMap.get(normalizedId);
      if (todayInfo && updatedTodays[todayInfo.index].isNew) {
        updatedTodays[todayInfo.index] = { ...updatedTodays[todayInfo.index], isNew: false };
        commit('setTodaysUpdates', updatedTodays);
      }

      // Update earlierUpdates if present
      const earlierInfo = earlierMap.get(normalizedId);
      if (earlierInfo && updatedEarlier[earlierInfo.index].isNew) {
        updatedEarlier[earlierInfo.index] = { ...updatedEarlier[earlierInfo.index], isNew: false };
        commit('setEarlierUpdates', updatedEarlier);
      }

      // Update unread counts
      if (unreadCountDecrement > 0) {
        const newUnreadCount = Math.max(0, state.unreadCount - unreadCountDecrement);
        commit('setUnreadCount', newUnreadCount);

        // Update unread counts by type
        const updateTypeValue = updateInfo.update.updateType?.updateType;
        if (updateTypeValue && state.unreadCountsByType[updateTypeValue]) {
          const newCountsByType = { ...state.unreadCountsByType };
          newCountsByType.all = Math.max(0, (newCountsByType.all || 0) - 1);
          newCountsByType[updateTypeValue] = Math.max(
            0,
            (newCountsByType[updateTypeValue] || 0) - 1,
          );
          commit('setUnreadCountsByType', newCountsByType);
        }
      }

      const accessToken = rootState.user.token;
      if (!accessToken) {
        console.error('No access token available for mark-read-update');
        dispatch('toast/error', 'Authentication required to mark update as read.', { root: true });
        return;
      }

      // Determine which API to call based on source
      const update = updateInfo.update;
      const isGivingApiNotification = update.source === 'giving-api';
      const isGeneralNotification = update.source === 'general';
      // Per-member row sources (giving-api, general) share the mark-read contract.
      const isRowNotification = isGivingApiNotification || isGeneralNotification;

      // Check if notification is already read - don't call API if already read
      const isAlreadyRead = !update.isNew || update.status === 'clicked' || update.clicked_at;

      if (isRowNotification && !isAlreadyRead) {
        // Extract recipientId from the update object.
        // ID format is "{source}-{notificationId}-{recipientId}" (giving-… / gen-…).
        const recipientId =
          update.recipientId || (updateId.includes('-') ? updateId.split('-')[2] : null);

        if (!recipientId) {
          console.error('No recipientId found for row notification:', updateId);
          dispatch('toast/error', 'Failed to mark notification as read: missing recipient ID.', {
            root: true,
          });
          return;
        }

        // Route to the owning service's mark-read endpoint.
        const markReadBaseUrl = isGeneralNotification
          ? getGeneralNotifBaseUrl()
          : getGivingApiBaseUrl();
        if (!markReadBaseUrl) {
          // A general notification can only be listed when the service is
          // configured, so this is a build without it: nothing to call.
          warnGeneralNotifMissing();
          return;
        }

        axios
          .post(
            `${markReadBaseUrl}/notifications/mark-read`,
            {
              recipientId: recipientId,
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
            },
          )
          .catch(error => {
            // If API call fails, revert the optimistic update
            console.error('Error marking row notification as read:', error);
            // Re-fetch updates to sync with server
            dispatch('getUpdates', 'all').catch(() => {
              dispatch('toast/error', 'Failed to mark notification as read.', { root: true });
            });
          });
      } else if (isRowNotification && isAlreadyRead) {
        // Already read, skip API call
        return;
      } else if (!isAlreadyRead) {
        // Call CMS API to mark as read (fire and forget - don't wait for response)
        // Only call if not already read
        axios
          .request(process.env.VUE_APP_API_URL + '/updates/mark-read-update', {
            method: 'post',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            data: {
              updateId,
            },
          })
          .catch(error => {
            // If API call fails, revert the optimistic update
            console.error('Error marking CMS update as read:', error);
            // Re-fetch updates to sync with server
            dispatch('getUpdates', 'all').catch(() => {
              dispatch('toast/error', 'Failed to mark read update.', { root: true });
            });
          });
      }
      // If already read (CMS), skip API call
    } catch (e) {
      console.error('Error marking update as read:', e);
      dispatch('toast/error', 'Failed to mark read update.', { root: true });
    }
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
